import { BookingFlowType } from 'integrations/metrics/types';
import { isEmpty, keyBy as indexBy, sortBy } from 'lodash';
import { TGlobalState } from 'reducers';
import { ServiceSelection } from 'reducers/selectedServices';
import { createSelector } from 'reselect';

import { TProfessional } from 'types/appointment';
import type { TService, TServiceOption } from 'types/service';

import { convertDecimalToSubunits } from 'utils/payments/money';

import {
  getAllProfessionals,
  getSelectedProfessional,
  hasTeamMembers,
  selectSelectedProfessional,
} from './professionals';

const getAvailableServices = (state: any): TService[] =>
  state?.appointment?.professional?.services || [];

const getAppointmentServicesGuids = (state: any): string[] =>
  state?.appointment?.service_guids;

/**
 * Returns a sum of all selected services prices
 */
export const getTotalServicesPrice = createSelector(
  getAvailableServices,
  getAppointmentServicesGuids,
  (availableServices, selectedServices): number =>
    availableServices.reduce((total: number, service: TService) => {
      if (selectedServices.includes(service.guid)) {
        return total + Number(service.price);
      }
      return total;
    }, 0),
);

export type TDepositSum = {
  totalDeposit: number;
  totalFee: number;
};

type TDepositText = {
  totalDeposit: number;
  totalFee: number;
};

type TPaymentSettings = {
  require_deposit: boolean;
  require_card: boolean;
  deposit_amount: number;
  deposit_processing_fee: number;
};

/**
 * Returns a sum of all deposits and card fees with correct currency formatting
 * e.g.: { totalDeposit: $54, totalFee: $2.10 }
 */
export const getTotalDeposit = createSelector(
  (state: any): TPaymentSettings => state.appointment.booking_payment_settings,
  (paymentSettings): TDepositText => {
    const { deposit_amount, deposit_processing_fee } = paymentSettings;

    return {
      totalDeposit: deposit_amount,
      totalFee: deposit_processing_fee,
    };
  },
);

export const getSelectedServiceCategory = (state: TGlobalState) =>
  state.selectedServiceCategory;

export const getAllServiceCategories = createSelector(
  getAllProfessionals,
  (professionals = []) => {
    const categories = new Set<string>();

    professionals.forEach((p) => {
      if (p.services?.length > 0) {
        p.services.forEach((s: any) => {
          if (s.category_name && s.category_name?.toLowerCase() !== 'none') {
            categories.add(s.category_name);
          }
        });
      }
    });

    return Array.from(categories);
  },
);

export const getSelectableServiceCategories = createSelector(
  getAllProfessionals,
  getSelectedProfessional,
  (allProfessionals = [], selectedProfessional) => {
    const categories = new Set<string>();
    const professionals = selectedProfessional
      ? [selectedProfessional]
      : allProfessionals;

    professionals.forEach((p: TProfessional) => {
      if (p.services?.length > 0) {
        p.services.forEach((s: any) => {
          if (s.category_name && s.category_name?.toLowerCase() !== 'none') {
            categories.add(s.category_name);
          }
        });
      }
    });

    return Array.from(categories).sort();
  },
);

const mergeOptions = (
  options1: TServiceOption[],
  options2: TServiceOption[],
): TServiceOption[] => {
  return [
    ...options1,
    ...options2.filter((o2) => !options1.some((o1) => o1.guid === o2.guid)),
  ];
};

export const getSelectableServices = createSelector(
  getAllProfessionals,
  getSelectedProfessional,
  (professionals = [], selectedProfessional) => {
    /**
     * Solo businesses with online booking disabled have their user entity not available
     * for booking ("available_for_booking" is false), that means users from those
     * businesses will be filter out from professional list (And no services would be displayed).
     * However, for solos, we are automatically selecting its sole user, so even when online booking
     * is disabled and this user is not available for booking, we are displaying all the services
     * provided by him.
     */
    if (selectedProfessional) {
      return selectedProfessional.services || [];
    }

    const serviceMap: Record<string, TService> = {};

    for (const professional of professionals) {
      for (const service of professional.services) {
        const selectableService = serviceMap[service.guid] || { ...service };

        selectableService.options = mergeOptions(
          selectableService.options || [],
          service.options || [],
        );
        serviceMap[service.guid] = selectableService;
      }
    }

    const services = Object.values(serviceMap);

    for (const service of services) {
      service.options = sortBy(service.options, ['ordering', 'name']);
    }

    return sortBy(services, ['ordering', 'name']);
  },
);

export const getAllServices = createSelector(
  getAllProfessionals,
  (professionals = []) => {
    const serviceMap: Record<string, TService> = {};

    for (const professional of professionals) {
      for (const service of professional.services) {
        const selectableService = serviceMap[service.guid] || { ...service };

        selectableService.options = mergeOptions(
          selectableService.options || [],
          service.options || [],
        );
        serviceMap[service.guid] = selectableService;
      }
    }
    return Object.values(serviceMap);
  },
);

export const getServicesInAppointment = createSelector(
  getAllServices,
  getAppointmentServicesGuids,
  (services = [], selectedServices) => {
    if (isEmpty(selectedServices)) return [];
    const servicesByGuid = indexBy(services, 'guid');
    const options = services
      .map(
        (service) =>
          service.options?.map((option) => {
            return {
              ...option,
              name: `${service.name} (${option.name})`,
            };
          }) || [],
      )
      .flat(1);
    const optionsByGuid = indexBy(options, 'guid');

    return selectedServices
      ?.map((guid) => servicesByGuid[guid] || optionsByGuid[guid])
      .filter(Boolean);
  },
);

export const getBookingFlowType = createSelector(
  selectSelectedProfessional,
  hasTeamMembers,
  (selectedProfessional, hasTeamMember) => {
    if (!hasTeamMember) return BookingFlowType.SERVICE_FIRST;
    if (selectedProfessional?.guid) return BookingFlowType.PROFESSIONAL_FIRST;

    return BookingFlowType.SERVICE_FIRST;
  },
);

const getSelectedServicesState = createSelector(
  (state: TGlobalState) => state?.selectedServices,
  (selectedServices) => selectedServices || [],
);

export const getSelectedServices = createSelector(
  getSelectedServicesState,
  (selectedServices) => selectedServices.map((s) => s.guid),
);

export const getServicesToBook = createSelector(
  getSelectedServicesState,
  (selectedServices) => selectedServices.map((s) => s.optionId || s.guid),
);

type TServiceToSelectedOption = Record<string, string | undefined>;

export const getMapServiceToSelectedOption = createSelector(
  getSelectedServicesState,
  (selectedServices) =>
    selectedServices.reduce((acc, s) => {
      acc[s.guid] = s.optionId;
      return acc;
    }, {} as TServiceToSelectedOption) || {},
);

export const getMapSelectedOptionToService = (
  state: TGlobalState,
): TServiceToSelectedOption =>
  state?.selectedServices?.reduce((acc, s) => {
    if (s.optionId) {
      acc[s.optionId] = s.guid;
    }
    return acc;
  }, {} as TServiceToSelectedOption) || {};

const getUnitPrice = (serviceOrOption: TService | TServiceOption) => {
  if (serviceOrOption.price_hidden) {
    return 0;
  }

  const priceInDecimal = Number(serviceOrOption.price);

  return convertDecimalToSubunits(priceInDecimal);
};

export const getEstimateUnitPrice = (
  allServices: TService[],
  serviceSelections: ServiceSelection[],
) => {
  return serviceSelections.reduce((acc, selection) => {
    const serviceId = selection.guid;
    const optionId = selection.optionId;
    const selectedService = allServices.find((s) => s.guid === serviceId);

    if (!selectedService) return acc; // This should never happen since user can't select a service that doesn't exist

    const selectedOption = optionId
      ? selectedService.options?.find((o) => o.guid === optionId)
      : undefined;

    if (optionId && !selectedOption) {
      // This should never happen since user can't select a service option that doesn't exist
      return acc;
    }

    const price = selectedOption
      ? getUnitPrice(selectedOption)
      : getUnitPrice(selectedService);

    return acc + price;
  }, 0);
};

export const getBookingEstimateUnitPrice = createSelector(
  getAllServices,
  getSelectedServicesState,
  getEstimateUnitPrice,
);
